import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  ViewList as ViewListIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  MedicalServices as MedicalServicesIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import momentPlugin from '@fullcalendar/moment';
// Import custom FullCalendar styles
import './fullcalendar.css';

// Get API URL from environment variables
import { API_URL } from '../../utils/apiConfig';
import { getOrganizationDateTimeSettings } from '../../utils/dateTimeUtils';
import { formatFailedActionMessage, formatSuccessActionMessage } from '../../utils/uiMessages';

const AppointmentCalendar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const calendarRef = useRef(null);
  const [appointments, setAppointments] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    clinicId: '',
    doctorId: '',
    status: '',
  });
  const [currentView, setCurrentView] = useState('dayGridMonth');
  const selectingRef = useRef(false);
  const [timeSettingsDialogOpen, setTimeSettingsDialogOpen] = useState(false);
  const [specificDateDialogOpen, setSpecificDateDialogOpen] = useState(false);
  const [selectedSpecificDate, setSelectedSpecificDate] = useState(null);
  const [tempDateSettings, setTempDateSettings] = useState({ isOpen: true, startTime: '09:00', endTime: '18:00' });
  const [timeFormat, setTimeFormat] = useState('12h'); // '12h' | '24h' from organization settings
  
  // Day names for display (0 = Sunday, 1 = Monday, etc.)
  const DAY_NAMES = [
    { value: 0, label: 'Sunday', key: 'sunday' },
    { value: 1, label: 'Monday', key: 'monday' },
    { value: 2, label: 'Tuesday', key: 'tuesday' },
    { value: 3, label: 'Wednesday', key: 'wednesday' },
    { value: 4, label: 'Thursday', key: 'thursday' },
    { value: 5, label: 'Friday', key: 'friday' },
    { value: 6, label: 'Saturday', key: 'saturday' },
  ];

  // Default time settings for each day
  const defaultTimeSettings = {
    monday: { isOpen: true, startTime: '09:00', endTime: '18:00' },
    tuesday: { isOpen: true, startTime: '09:00', endTime: '18:00' },
    wednesday: { isOpen: true, startTime: '09:00', endTime: '18:00' },
    thursday: { isOpen: true, startTime: '09:00', endTime: '18:00' },
    friday: { isOpen: true, startTime: '09:00', endTime: '18:00' },
    saturday: { isOpen: true, startTime: '09:00', endTime: '16:00' },
    sunday: { isOpen: false, startTime: '10:00', endTime: '14:00' },
  };

  // Load time settings from localStorage
  const loadTimeSettings = () => {
    try {
      const saved = localStorage.getItem('calendarTimeSettings');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (err) {
      console.error('Error loading time settings:', err);
    }
    return defaultTimeSettings;
  };

  // Load specific date settings from localStorage
  const loadSpecificDateSettings = () => {
    try {
      const saved = localStorage.getItem('calendarSpecificDateSettings');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (err) {
      console.error('Error loading specific date settings:', err);
    }
    return {};
  };

  const [timeSettings, setTimeSettings] = useState(loadTimeSettings);
  const [specificDateSettings, setSpecificDateSettings] = useState(loadSpecificDateSettings);

  // Appointment statuses for filtering
  const APPOINTMENT_STATUSES = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'no-show', label: 'No Show' },
  ];

  // Fetch appointments
  const fetchAppointments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Build query parameters for filtering
      const queryParams = new URLSearchParams();
      
      if (filters.clinicId) {
        queryParams.append('clinic', filters.clinicId);
      }
      
      if (filters.doctorId) {
        queryParams.append('dentist', filters.doctorId);
      }
      
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      
      // Remove pagination to get all appointments for calendar
      queryParams.append('limit', '1000'); // Get up to 1000 appointments
      queryParams.append('page', '1');
      
      // Add timestamp to prevent caching (append to existing params)
      const baseUrl = `${API_URL}/appointments${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const separator = queryParams.toString() ? '&' : '?';
      const url = `${baseUrl}${separator}_t=${Date.now()}`;

      // Real API call
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      const data = response.data.data || [];
      
      // Format appointments for FullCalendar
      const formattedAppointments = data.map(appointment => {
        // Get color based on custom color or status
        const getEventColor = (status, customColor) => {
          // If custom color is provided, use it
          if (customColor) {
            return customColor;
          }
          // Otherwise, use default color based on status
          switch (status) {
            case 'scheduled':
              return '#3f51b5'; // indigo
            case 'confirmed':
              return '#2196f3'; // blue
            case 'completed':
              return '#4caf50'; // green
            case 'cancelled':
              return '#f44336'; // red
            case 'no-show':
              return '#ff9800'; // orange
            default:
              return '#3174ad'; // default blue
          }
        };

        const eventColor = getEventColor(appointment.status, appointment.color);

        // Parse dates safely
        let startDate;
        let endDate;
        
        try {
          startDate = appointment.appointmentDate 
            ? new Date(appointment.appointmentDate) 
            : null;
          
          if (!startDate || isNaN(startDate.getTime())) {
            console.warn('Invalid appointment date:', appointment.appointmentDate);
            return null;
          }
          
          endDate = appointment.endTime 
            ? new Date(appointment.endTime)
            : new Date(startDate.getTime() + (appointment.duration || 30) * 60000);
          
          if (isNaN(endDate.getTime())) {
            endDate = new Date(startDate.getTime() + (appointment.duration || 30) * 60000);
          }
        } catch (error) {
          console.error('Error parsing dates for appointment:', appointment._id, error);
          return null;
        }

        return {
          id: appointment._id,
          title: `${appointment.patient?.name || 'Unknown'} - ${appointment.appointmentType || 'Consultation'}`,
          start: startDate,
          end: endDate,
          backgroundColor: eventColor,
          borderColor: eventColor,
          textColor: '#ffffff',
          extendedProps: {
            patientId: appointment.patient?._id,
            patientName: appointment.patient?.name || 'Unknown',
            doctorId: appointment.dentist?._id,
            doctorName: `${appointment.dentist?.firstName || ''} ${appointment.dentist?.lastName || ''}`.trim() || 'Unknown',
            clinicId: appointment.clinic?._id,
            clinicName: appointment.clinic?.name || 'Unknown',
            status: appointment.status,
            type: appointment.appointmentType,
            duration: appointment.duration || 30,
            notes: appointment.notes,
            reasonForVisit: appointment.reasonForVisit,
            color: appointment.color,
          }
        };
      }).filter(appointment => appointment !== null); // Remove null entries
      
      setAppointments(formattedAppointments);
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError(formatFailedActionMessage('load', 'appointments'));
    } finally {
      setLoading(false);
    }
  }, [filters.clinicId, filters.doctorId, filters.status]);

  // Fetch clinics
  const fetchClinics = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await axios.get(`${API_URL}/clinics`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setClinics(response.data.data);
    } catch (err) {
      console.error('Error fetching clinics:', err);
      toast.error(formatFailedActionMessage('load', 'clinics'));
    }
  }, []);
  
  // Fetch doctors
  const fetchDoctors = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await axios.get(`${API_URL}/staff`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Filter to only show dentists
      const dentists = response.data.data.filter(staff => staff.role === 'dentist');
      setDoctors(dentists);
    } catch (err) {
      console.error('Error fetching doctors:', err);
      toast.error(formatFailedActionMessage('load', 'doctors'));
    }
  }, []);

  useEffect(() => {
    fetchAppointments();
    fetchClinics();
    fetchDoctors();
  }, [fetchAppointments, fetchClinics, fetchDoctors]);

  // Load organization time format (12h/24h) for calendar display
  useEffect(() => {
    getOrganizationDateTimeSettings(true).then((s) => {
      setTimeFormat(s.timeFormat === '24h' ? '24h' : '12h');
    });
  }, []);
  useEffect(() => {
    if (location.pathname === '/calendar') {
      getOrganizationDateTimeSettings(true).then((s) => {
        setTimeFormat(s.timeFormat === '24h' ? '24h' : '12h');
      });
    }
  }, [location.pathname]);

  // Refresh appointments when returning to calendar page
  useEffect(() => {
    if (location.pathname === '/calendar') {
      // Small delay to ensure navigation is complete and prevent stale data
      const timer = setTimeout(() => {
        fetchAppointments();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, fetchAppointments]);

  // Handle event click
  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event;
    setSelectedEvent({
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      ...event.extendedProps
    });
    setEventDialogOpen(true);
  };

  // Format date and time for tooltip (respects organization 12h/24h setting)
  const hour12 = timeFormat === '12h';
  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12
    });
  };

  // Create tooltip text for event
  const createEventTooltip = (event) => {
    const props = event.extendedProps || {};
    const start = formatDateTime(event.start);
    const end = formatDateTime(event.end);
    
    let tooltip = `Patient: ${props.patientName || 'Unknown'}\n`;
    tooltip += `Type: ${props.type || 'Consultation'}\n`;
    tooltip += `Doctor: ${props.doctorName || 'Unknown'}\n`;
    tooltip += `Clinic: ${props.clinicName || 'Unknown'}\n`;
    tooltip += `Status: ${props.status || 'scheduled'}\n`;
    tooltip += `Start: ${start}\n`;
    tooltip += `End: ${end}\n`;
    tooltip += `Duration: ${props.duration || 30} minutes\n`;
    if (props.reasonForVisit) {
      tooltip += `Reason: ${props.reasonForVisit}\n`;
    }
    if (props.notes) {
      tooltip += `Notes: ${props.notes}`;
    }
    return tooltip;
  };

  // Handle event mount - add tooltip
  const handleEventDidMount = (arg) => {
    const event = arg.event;
    const el = arg.el;
    
    // Add title attribute for native browser tooltip
    const tooltipText = createEventTooltip(event);
    el.setAttribute('title', tooltipText);
    
    // Add custom tooltip using MUI Tooltip if needed (optional enhancement)
    // For now, using native title attribute is simpler and works well
  };

  // Custom event content renderer - add warning icon for events with notes
  const renderEventContent = (arg) => {
    const event = arg.event;
    const props = event.extendedProps || {};
    const hasNotes = props.notes && props.notes.trim().length > 0;
    
    // Create a wrapper div
    const wrapper = document.createElement('div');
    wrapper.style.display = 'flex';
    wrapper.style.alignItems = 'center';
    wrapper.style.gap = '4px';
    
    // Add warning icon if notes exist
    if (hasNotes) {
      const iconWrapper = document.createElement('span');
      iconWrapper.style.display = 'inline-flex';
      iconWrapper.style.alignItems = 'center';
      iconWrapper.style.marginRight = '2px';
      iconWrapper.innerHTML = '⚠️'; // Using emoji for simplicity, can be replaced with SVG icon
      iconWrapper.style.fontSize = '14px';
      wrapper.appendChild(iconWrapper);
    }
    
    // Add event title
    const titleSpan = document.createElement('span');
    titleSpan.textContent = event.title;
    titleSpan.style.flex = '1';
    titleSpan.style.overflow = 'hidden';
    titleSpan.style.textOverflow = 'ellipsis';
    wrapper.appendChild(titleSpan);
    
    return { domNodes: [wrapper] };
  };

  // Handle event dialog close
  const handleEventDialogClose = () => {
    setEventDialogOpen(false);
    setSelectedEvent(null);
  };

  // Handle view appointment details
  const handleViewAppointment = () => {
    if (selectedEvent) {
      navigate(`/appointments/${selectedEvent.id}`);
    }
    handleEventDialogClose();
  };

  // Handle edit appointment
  const handleEditAppointment = () => {
    if (selectedEvent) {
      navigate(`/appointments/${selectedEvent.id}/edit`);
    }
    handleEventDialogClose();
  };

  // Handle add treatment from appointment
  const handleAddTreatment = () => {
    if (selectedEvent) {
      // Format date and time for the treatment form
      const appointmentDate = new Date(selectedEvent.start);
      const dateStr = appointmentDate.toISOString().split('T')[0]; // YYYY-MM-DD
      const timeStr = appointmentDate.toTimeString().split(' ')[0].substring(0, 5); // HH:MM
      
      // Get duration from appointment (in minutes)
      const duration = selectedEvent.duration || 30; // Default to 30 minutes if not specified
      
      // Navigate to add treatment with pre-filled patient, date, time, and duration
      navigate(`/treatments/add?patientId=${selectedEvent.patientId}&date=${dateStr}&time=${timeStr}&duration=${duration}`);
    }
    handleEventDialogClose();
  };

  // Handle open patient details from appointment
  const handleViewPatient = () => {
    if (selectedEvent && selectedEvent.patientId) {
      navigate(`/patients/${selectedEvent.patientId}`);
      handleEventDialogClose();
    } else {
      toast.error('Patient information is not available for this appointment');
    }
  };

  // Track last click time for double-click detection
  const lastClickRef = useRef({ date: null, time: 0, timeout: null, viewType: null, dateStr: null });

  // Handle date click (switch to day view when clicking on a day in month view)
  const handleDateClick = (arg) => {
    const clickedDate = arg.date;
    const now = Date.now();
    const viewType = arg.view.type;
    const dateStr = arg.dateStr;
    
    // Check if Ctrl/Cmd key is pressed - if so, open date settings dialog immediately
    if (arg.jsEvent && (arg.jsEvent.ctrlKey || arg.jsEvent.metaKey)) {
      // Clear any pending single click
      if (lastClickRef.current.timeout) {
        clearTimeout(lastClickRef.current.timeout);
        lastClickRef.current.timeout = null;
      }
      if (clickedDate) {
        handleOpenSpecificDateDialog(clickedDate);
      }
      lastClickRef.current = { date: null, time: 0, timeout: null, viewType: null, dateStr: null };
      return;
    }
    
    // Check if this is a double-click (within 400ms of previous click on same date)
    if (lastClickRef.current.date && 
        clickedDate && 
        Math.abs(lastClickRef.current.date.getTime() - clickedDate.getTime()) < 1000 && // Same date (within 1 second)
        now - lastClickRef.current.time < 400) {
      // Double-click detected - cancel single click and open date settings dialog
      if (lastClickRef.current.timeout) {
        clearTimeout(lastClickRef.current.timeout);
        lastClickRef.current.timeout = null;
      }
      handleOpenSpecificDateDialog(clickedDate);
      lastClickRef.current = { date: null, time: 0, timeout: null, viewType: null, dateStr: null };
      return;
    }
    
    // Clear any existing timeout
    if (lastClickRef.current.timeout) {
      clearTimeout(lastClickRef.current.timeout);
    }
    
    // Store this click for double-click detection
    if (clickedDate) {
      lastClickRef.current.date = clickedDate;
      lastClickRef.current.time = now;
      lastClickRef.current.viewType = viewType;
      lastClickRef.current.dateStr = dateStr;
      
      // Set timeout to handle single click after double-click detection period
      lastClickRef.current.timeout = setTimeout(() => {
        // Single click - perform normal action
        // Only switch view in month view
        if (lastClickRef.current.viewType === 'dayGridMonth') {
          const calendarApi = calendarRef.current?.getApi();
          if (calendarApi && lastClickRef.current.dateStr) {
            calendarApi.changeView('timeGridDay', lastClickRef.current.dateStr);
            setCurrentView('timeGridDay');
          }
        }
        // In week/day views, do nothing - let select handle it
        
        // Clear the click reference
        lastClickRef.current = { date: null, time: 0, timeout: null, viewType: null, dateStr: null };
      }, 400); // Wait 400ms to see if there's a second click
    } else {
      // No date clicked, perform normal action immediately
      if (viewType === 'dayGridMonth') {
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) {
          calendarApi.changeView('timeGridDay', dateStr);
          setCurrentView('timeGridDay');
        }
      }
    }
  };

  // Handle date/time slot selection (create new appointment)
  const handleDateSelect = (selectInfo) => {
    // Prevent multiple rapid calls
    if (selectingRef.current) {
      selectInfo.view.calendar.unselect();
      return;
    }

    // Only handle selection in week/day views
    if (selectInfo.view.type === 'dayGridMonth') {
      selectInfo.view.calendar.unselect();
      return;
    }

    if (!selectInfo.start) {
      selectInfo.view.calendar.unselect();
      return;
    }

    // Check if the selected time is within business hours
    // selectAllow should have already checked this, but double-check here
    const date = new Date(selectInfo.start);
    const timeSettings = getTimeSettingsForDate(date);
    
    if (!timeSettings) {
      selectInfo.view.calendar.unselect();
      toast.error('This date is closed for appointments');
      selectingRef.current = false;
      return;
    }
    
    // Get the time of the selected slot
    const selectedHour = date.getHours();
    const selectedMinute = date.getMinutes();
    const selectedTimeMinutes = selectedHour * 60 + selectedMinute;
    
    // Parse business hours
    const [startHour, startMin] = timeSettings.startTime.split(':').map(Number);
    const [endHour, endMin] = timeSettings.endTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    
    // Check if selection is within business hours
    if (selectedTimeMinutes < startMinutes || selectedTimeMinutes >= endMinutes) {
      selectInfo.view.calendar.unselect();
      const startTimeStr = timeSettings.startTime;
      const endTimeStr = timeSettings.endTime;
      toast.error(`This time slot is closed. Working hours: ${startTimeStr} - ${endTimeStr}`);
      selectingRef.current = false;
      return;
    }

    selectingRef.current = true;

    // Calculate duration
    const endTime = selectInfo.end || new Date(selectInfo.start.getTime() + 30 * 60000);
    const durationMs = endTime.getTime() - selectInfo.start.getTime();
    const durationMinutes = Math.max(15, Math.round(durationMs / 60000));
    
    // Clear selection immediately
    selectInfo.view.calendar.unselect();
    
    // Use requestAnimationFrame to ensure navigation happens after render
    requestAnimationFrame(() => {
      navigate(`/appointments/add?startDate=${encodeURIComponent(selectInfo.start.toISOString())}&duration=${durationMinutes}`);
      selectingRef.current = false;
    });
  };

  // Handle view change
  const handleViewChange = (viewInfo) => {
    setCurrentView(viewInfo.view.type);
  };

  // Apply closed slot classes after calendar renders
  useEffect(() => {
    if (!calendarRef.current) return;
    
    // Helper functions for overlay-based closed slots
    const parseTimeToMinutes = (t) => {
      const [hh, mm] = t.split(':').map(Number);
      return hh * 60 + mm;
    };

    const buildTimeToYMap = (calendarRoot) => {
      const rootRect = calendarRoot.getBoundingClientRect();

      // Find all .fc-timegrid-slot-lane[data-time] elements
      const slats = Array.from(
        calendarRoot.querySelectorAll('.fc-timegrid-slot-lane[data-time]')
      );

      if (!slats.length) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('No .fc-timegrid-slot-lane[data-time] found for Y mapping');
        }
        return { map: new Map(), timeKeys: [] };
      }

      const timeKeys = slats.map(el => el.getAttribute('data-time'));
      const map = new Map();

      for (let i = 0; i < slats.length; i++) {
        const cur = slats[i];
        const next = slats[i + 1];

        const curRect = cur.getBoundingClientRect();
        const top = curRect.top - rootRect.top;
        const height = next
          ? next.getBoundingClientRect().top - curRect.top
          : curRect.height;

        map.set(cur.getAttribute('data-time'), { top, height });
      }

      return { map, timeKeys };
    };

    const clearClosedOverlays = (calendarRoot) => {
      calendarRoot
        .querySelectorAll('.fc-slot-closed-overlay')
        .forEach(n => n.remove());
    };

    const applyClosedSlotClasses = () => {
      const calendarApi = calendarRef.current.getApi();
      if (!calendarApi) return;
      
      const view = calendarApi.view;
      if (!view || (!view.type.includes('timeGrid'))) {
        return;
      }
      
      const calendarEl = document.querySelector('.fc-timegrid') || document.querySelector('.fc-view-harness');
      if (!calendarEl) return;
      
      let closedSlotsCount = 0;
      
      if (view.type === 'timeGridWeek') {
        // Clear previous overlays
        clearClosedOverlays(calendarEl);
        
        // 1. Build time-to-Y map using .fc-timegrid-slot-lane[data-time]
        const { map: timeToY, timeKeys } = buildTimeToYMap(calendarEl);
        if (!timeToY.size || !timeKeys.length) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Could not build time-to-Y map for week view');
          }
          return;
        }
        
        // 2. Get active dates for the week
        const activeStart = new Date(view.activeStart);
        const activeEnd = new Date(view.activeEnd);
        const weekDates = [];
        const currentDate = new Date(activeStart);
        while (currentDate < activeEnd) {
          weekDates.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
        }
        
        // 3. Process each day column
        weekDates.forEach((date) => {
          const dateStr = formatDateForStorage(date);
          const dayOfWeek = date.getDay();
          const dayKey = DAY_NAMES.find(d => d.value === dayOfWeek)?.key;
          
          // Find the day column
          const dayCol = calendarEl.querySelector(`.fc-timegrid-col[data-date="${dateStr}"]`);
          if (!dayCol) return;
          
          // Get frame and bg containers
          const frame = dayCol.querySelector('.fc-timegrid-col-frame');
          const bg = dayCol.querySelector('.fc-timegrid-col-bg');
          
          // Get host container (.fc-timegrid-col-frame or dayCol itself)
          const host = frame || dayCol;
          if (!host) return;
          
          // Ensure relative positioning
          const hostStyle = getComputedStyle(host);
          if (hostStyle.position === 'static') {
            host.style.position = 'relative';
          }
          
          // Determine closed intervals for this date
          let isDayClosed = false;
          let workingStart = null;
          let workingEnd = null;
          
          // PRIORITY 1: Check specific date settings
          if (specificDateSettings && specificDateSettings[dateStr]) {
            const specific = specificDateSettings[dateStr];
            if (specific.isOpen === false) {
              isDayClosed = true;
            } else {
              workingStart = specific.startTime || '09:00';
              workingEnd = specific.endTime || '18:00';
            }
          }
          
          // PRIORITY 2: Fall back to day of week settings
          if (!isDayClosed && !workingStart && dayKey && timeSettings && timeSettings[dayKey]) {
            const daySettings = timeSettings[dayKey];
            if (daySettings.isOpen === false) {
              isDayClosed = true;
            } else if (daySettings.isOpen === true) {
              workingStart = daySettings.startTime || '09:00';
              workingEnd = daySettings.endTime || '18:00';
            }
          }
          
          // If day is completely closed or no settings, overlay entire visible range
          if (isDayClosed || (!workingStart && (!dayKey || !timeSettings || !timeSettings[dayKey]))) {
            if (timeKeys.length > 0) {
              const firstTime = timeKeys[0];
              const lastTime = timeKeys[timeKeys.length - 1];
              const firstY = timeToY.get(firstTime);
              const lastY = timeToY.get(lastTime);
              if (firstY && lastY) {
                const top = firstY.top;
                const height = (lastY.top + lastY.height) - top;
                
                const overlay = document.createElement('div');
                overlay.className = 'fc-slot-closed-overlay';
                overlay.style.position = 'absolute';
                overlay.style.left = '0';
                overlay.style.right = '0';
                overlay.style.top = `${top}px`;
                overlay.style.height = `${height}px`;
                overlay.style.pointerEvents = 'none';
                overlay.style.zIndex = '5'; // Temporarily higher for testing
                // Append to bg if available, otherwise to frame
                const overlayHost = bg || host;
                overlayHost.appendChild(overlay);
                closedSlotsCount++;
              }
            }
          } else if (workingStart && workingEnd) {
            // Day is open - find closed intervals outside working hours
            const [startHour, startMin] = workingStart.split(':').map(Number);
            const [endHour, endMin] = workingEnd.split(':').map(Number);
            const startMinutes = startHour * 60 + startMin;
            const endMinutes = endHour * 60 + endMin;
            
            // Find all closed slots by checking each time slot
            const closedSlots = [];
            let currentRangeStart = null;
            let currentRangeStartY = null;
            
            for (let i = 0; i < timeKeys.length; i++) {
              const timeKey = timeKeys[i];
              const timeMinutes = parseTimeToMinutes(timeKey);
              const slotY = timeToY.get(timeKey);
              
              if (!slotY) continue;
              
              // Check if this slot is closed (before start or at/after end)
              const isClosed = timeMinutes < startMinutes || timeMinutes >= endMinutes;
              
              if (isClosed) {
                // Start a new range or continue current range
                if (currentRangeStart === null) {
                  currentRangeStart = timeKey;
                  currentRangeStartY = slotY;
                }
              } else {
                // Slot is open - close current range if exists
                if (currentRangeStart !== null && currentRangeStartY) {
                  const prevSlotY = timeToY.get(timeKeys[i - 1]);
                  if (prevSlotY) {
                    closedSlots.push({
                      start: currentRangeStart,
                      startY: currentRangeStartY,
                      end: timeKeys[i - 1],
                      endY: prevSlotY
                    });
                  }
                  currentRangeStart = null;
                  currentRangeStartY = null;
                }
              }
            }
            
            // Close any remaining range at the end
            if (currentRangeStart !== null && currentRangeStartY) {
              const lastTime = timeKeys[timeKeys.length - 1];
              const lastY = timeToY.get(lastTime);
              if (lastY) {
                closedSlots.push({
                  start: currentRangeStart,
                  startY: currentRangeStartY,
                  end: lastTime,
                  endY: lastY
                });
              }
            }
            
            // Create overlays for closed ranges
            for (const range of closedSlots) {
              const top = range.startY.top;
              // Calculate height: from start slot top to end slot bottom
              const endIndex = timeKeys.indexOf(range.end);
              const nextKey = timeKeys[endIndex + 1];
              const height = nextKey && timeToY.has(nextKey)
                ? timeToY.get(nextKey).top - top
                : (range.endY.top + range.endY.height) - top;
              
            if (height <= 0) continue;
            
            const overlay = document.createElement('div');
            overlay.className = 'fc-slot-closed-overlay';
            overlay.style.position = 'absolute';
            overlay.style.left = '0';
            overlay.style.right = '0';
            overlay.style.top = `${top}px`;
            overlay.style.height = `${height}px`;
            overlay.style.pointerEvents = 'none';
            overlay.style.zIndex = '5'; // Temporarily higher for testing
            // Append to bg if available, otherwise to frame
            const overlayHost = bg || host;
            overlayHost.appendChild(overlay);
            closedSlotsCount++;
            }
          }
        });
        
        if (process.env.NODE_ENV === 'development') {
          console.debug('Applied overlays for week view. Closed ranges:', closedSlotsCount);
        }
        
        return; // Early return for week view
      } else if (view.type === 'timeGridDay') {
        // Day view: use overlays on the appointment column, not the time axis
        clearClosedOverlays(calendarEl);
        
        const date = new Date(view.activeStart);
        const dateStr = formatDateForStorage(date);
        const dayOfWeek = date.getDay();
        const dayKey = DAY_NAMES.find(d => d.value === dayOfWeek)?.key;
        
        // Find the day column (appointment column, not time axis)
        // In day view, the appointment column has data-date, axis does not
        const dayCol =
          calendarEl.querySelector('.fc-day.fc-timegrid-col[data-date]') ||
          calendarEl.querySelector('.fc-timegrid-col[data-date]') ||
          calendarEl.querySelector('.fc-timegrid-col:not(.fc-timegrid-axis)');
        
        // Safety check: ensure we didn't select the axis
        if (!dayCol || dayCol.classList.contains('fc-timegrid-axis')) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Failed to find appointment day column or selected axis', dayCol);
          }
          return;
        }
        
        // Get frame and bg containers
        const frame = dayCol.querySelector('.fc-timegrid-col-frame');
        const bg = dayCol.querySelector('.fc-timegrid-col-bg');
        
        // Use frame as host (most stable container)
        const host = frame || bg || dayCol;
        if (!host) return;
        
        // Ensure relative positioning
        const hostStyle = getComputedStyle(host);
        if (hostStyle.position === 'static') {
          host.style.position = 'relative';
        }
        
        // Find slot cells from the common slots table (not from dayCol)
        // In day view, slots are in .fc-timegrid-slots table, not inside dayCol
        const allSlotCells = Array.from(
          calendarEl.querySelectorAll('.fc-timegrid-slots td[data-time]')
        );
        
        // Filter out time labels
        const slotCells = allSlotCells.filter(td => !td.classList.contains('fc-timegrid-slot-label'));
        
        if (slotCells.length === 0) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('No slot cells found in day view');
            console.debug('allSlotCells.length:', allSlotCells.length);
          }
          return;
        }
        
        // Debug: log slot cells info
        if (process.env.NODE_ENV === 'development') {
          console.debug('allSlotCells:', allSlotCells.length, 'slotCells:', slotCells.length);
          console.debug('slotCells[0]:', slotCells[0]?.outerHTML?.slice(0, 120));
        }
        
        // Get scroll container for correct coordinate calculation
        const scroller = calendarEl.querySelector('.fc-timegrid-body .fc-scroller');
        const base = scroller || host;
        const baseRect = base.getBoundingClientRect();
        
        // Build time-to-Y map relative to scroller/host
        const timeToY = new Map();
        const timeKeys = [];
        
        for (let i = 0; i < slotCells.length; i++) {
          const cell = slotCells[i];
          const timeStr = cell.getAttribute('data-time');
          if (!timeStr) continue;
          
          const r = cell.getBoundingClientRect();
          const top = r.top - baseRect.top;
          
          const next = slotCells[i + 1];
          const height = next
            ? next.getBoundingClientRect().top - r.top
            : r.height;
          
          timeToY.set(timeStr, { top, height });
          timeKeys.push(timeStr);
        }
        
        if (timeKeys.length === 0) return;
        
        // Debug: verify coordinates
        if (process.env.NODE_ENV === 'development') {
          const firstTime = timeKeys[0];
          const firstY = timeToY.get(firstTime);
          console.debug('slotCells.length:', slotCells.length);
          console.debug('host.getBoundingClientRect().height:', host.getBoundingClientRect().height);
          console.debug('first time:', firstTime, 'top:', firstY?.top, 'height:', firstY?.height);
        }
        
        // Determine closed intervals for this date
        let isDayClosed = false;
        let workingStart = null;
        let workingEnd = null;
        
        // PRIORITY 1: Check specific date settings
        if (specificDateSettings && specificDateSettings[dateStr]) {
          const specific = specificDateSettings[dateStr];
          if (specific.isOpen === false) {
            isDayClosed = true;
          } else {
            workingStart = specific.startTime || '09:00';
            workingEnd = specific.endTime || '18:00';
          }
        }
        
        // PRIORITY 2: Fall back to day of week settings
        if (!isDayClosed && !workingStart && dayKey && timeSettings && timeSettings[dayKey]) {
          const daySettings = timeSettings[dayKey];
          if (daySettings.isOpen === false) {
            isDayClosed = true;
          } else if (daySettings.isOpen === true) {
            workingStart = daySettings.startTime || '09:00';
            workingEnd = daySettings.endTime || '18:00';
          }
        }
        
        // If day is completely closed or no settings, overlay entire visible range
        if (isDayClosed || (!workingStart && (!dayKey || !timeSettings || !timeSettings[dayKey]))) {
          if (timeKeys.length > 0) {
            const firstTime = timeKeys[0];
            const lastTime = timeKeys[timeKeys.length - 1];
            const firstY = timeToY.get(firstTime);
            const lastY = timeToY.get(lastTime);
            if (firstY && lastY) {
              const top = firstY.top;
              const height = (lastY.top + lastY.height) - top;
              
              const overlay = document.createElement('div');
              overlay.className = 'fc-slot-closed-overlay';
              overlay.style.position = 'absolute';
              overlay.style.left = '0';
              overlay.style.right = '0';
              overlay.style.top = `${top}px`;
              overlay.style.height = `${height}px`;
              overlay.style.pointerEvents = 'none';
              overlay.style.zIndex = '5'; // Temporarily higher for testing
              // Append to bg if available, otherwise to frame
              const overlayHost = bg || host;
              overlayHost.appendChild(overlay);
              closedSlotsCount++;
            }
          }
        } else if (workingStart && workingEnd) {
          // Day is open - find closed intervals outside working hours
          const [startHour, startMin] = workingStart.split(':').map(Number);
          const [endHour, endMin] = workingEnd.split(':').map(Number);
          const startMinutes = startHour * 60 + startMin;
          const endMinutes = endHour * 60 + endMin;
          
          // Find all closed slots by checking each time slot
          const closedSlots = [];
          let currentRangeStart = null;
          let currentRangeStartY = null;
          
          for (let i = 0; i < timeKeys.length; i++) {
            const timeKey = timeKeys[i];
            const timeMinutes = parseTimeToMinutes(timeKey);
            const slotY = timeToY.get(timeKey);
            
            if (!slotY) continue;
            
            // Check if this slot is closed (before start or at/after end)
            const isClosed = timeMinutes < startMinutes || timeMinutes >= endMinutes;
            
            if (isClosed) {
              // Start a new range or continue current range
              if (currentRangeStart === null) {
                currentRangeStart = timeKey;
                currentRangeStartY = slotY;
              }
            } else {
              // Slot is open - close current range if exists
              if (currentRangeStart !== null && currentRangeStartY) {
                const prevSlotY = timeToY.get(timeKeys[i - 1]);
                if (prevSlotY) {
                  closedSlots.push({
                    start: currentRangeStart,
                    startY: currentRangeStartY,
                    end: timeKeys[i - 1],
                    endY: prevSlotY
                  });
                }
                currentRangeStart = null;
                currentRangeStartY = null;
              }
            }
          }
          
          // Close any remaining range at the end
          if (currentRangeStart !== null && currentRangeStartY) {
            const lastTime = timeKeys[timeKeys.length - 1];
            const lastY = timeToY.get(lastTime);
            if (lastY) {
              closedSlots.push({
                start: currentRangeStart,
                startY: currentRangeStartY,
                end: lastTime,
                endY: lastY
              });
            }
          }
          
          // Create overlays for closed ranges
          for (const range of closedSlots) {
            const top = range.startY.top;
            const endIndex = timeKeys.indexOf(range.end);
            const nextKey = timeKeys[endIndex + 1];
            const height = nextKey && timeToY.has(nextKey)
              ? timeToY.get(nextKey).top - top
              : (range.endY.top + range.endY.height) - top;
            
            if (height <= 0) continue;
            
            const overlay = document.createElement('div');
            overlay.className = 'fc-slot-closed-overlay';
            overlay.style.position = 'absolute';
            overlay.style.left = '0';
            overlay.style.right = '0';
            overlay.style.top = `${top}px`;
            overlay.style.height = `${height}px`;
            overlay.style.pointerEvents = 'none';
            overlay.style.zIndex = '5'; // Temporarily higher for testing
            // Append to bg if available, otherwise to frame
            const overlayHost = bg || host;
            overlayHost.appendChild(overlay);
            closedSlotsCount++;
          }
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.debug('Applied overlays for day view. Closed ranges:', closedSlotsCount);
        }
        
        return;
      } else if (view.type === 'dayGridMonth') {
        // Month view: highlight closed days
        const monthDates = [];
        let currentDate = new Date(view.activeStart);
        const endDate = new Date(view.activeEnd);
        
        // Get all dates in the visible month
        while (currentDate < endDate) {
          monthDates.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
        }
        
        monthDates.forEach((date) => {
          const dateStr = formatDateForStorage(date);
          const dayOfWeek = date.getDay();
          const dayKey = DAY_NAMES.find(d => d.value === dayOfWeek)?.key;
          
          // Find the day cell
          const dayCell = calendarEl.querySelector(`.fc-daygrid-day[data-date="${dateStr}"]`);
          if (!dayCell) return;
          
          // Remove existing closed class
          dayCell.classList.remove('fc-day-closed');
          
          // Determine if day is closed
          let isDayClosed = false;
          
          // PRIORITY 1: Check specific date settings
          if (specificDateSettings && specificDateSettings[dateStr]) {
            const specific = specificDateSettings[dateStr];
            if (specific.isOpen === false) {
              isDayClosed = true;
            }
          }
          
          // PRIORITY 2: Fall back to day of week settings
          if (!isDayClosed && dayKey && timeSettings && timeSettings[dayKey]) {
            const daySettings = timeSettings[dayKey];
            if (daySettings.isOpen === false) {
              isDayClosed = true;
            }
          }
          
          // PRIORITY 3: If no settings at all for this day
          if (!isDayClosed && (!dayKey || !timeSettings || !timeSettings[dayKey])) {
            isDayClosed = true;
          }
          
          // Apply closed class
          if (isDayClosed) {
            dayCell.classList.add('fc-day-closed');
            closedSlotsCount++;
          }
        });
        
        if (process.env.NODE_ENV === 'development') {
          console.debug('Applied closed styling for month view. Closed days:', closedSlotsCount);
        }
        
        return;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.debug('Applied classes. Closed slots:', closedSlotsCount);
      }
    };
    
    // Schedule overlay application using requestAnimationFrame
    let rafId = null;
    const scheduleApply = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        applyClosedSlotClasses();
        rafId = null;
      });
    };
    
    // Initial apply
    scheduleApply();
    
    // Also apply when view changes or events are rendered
    const calendarApi = calendarRef.current.getApi();
    if (calendarApi) {
      calendarApi.on('datesSet', scheduleApply);
      calendarApi.on('eventsSet', scheduleApply);
      calendarApi.on('viewDidMount', scheduleApply);
    }
    
    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      if (calendarApi) {
        calendarApi.off('datesSet', scheduleApply);
        calendarApi.off('eventsSet', scheduleApply);
        calendarApi.off('viewDidMount', scheduleApply);
      }
    };
  }, [specificDateSettings, timeSettings, currentView, appointments.length, DAY_NAMES]);

  // Handle event drop (drag & drop)
  const handleEventDrop = async (dropInfo) => {
    const event = dropInfo.event;
    const appointmentId = event.id;
    const newStart = event.start;
    const newEnd = event.end || new Date(newStart.getTime() + (event.extendedProps.duration || 30) * 60000);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Update appointment via API
      await axios.put(
        `${API_URL}/appointments/${appointmentId}`,
        {
          appointmentDate: newStart.toISOString(),
          endTime: newEnd.toISOString(),
          duration: Math.round((newEnd - newStart) / 60000), // Convert to minutes
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success(formatSuccessActionMessage('updated', 'Appointment'));
      fetchAppointments(); // Refresh to get updated data
    } catch (err) {
      console.error('Error updating appointment:', err);
      toast.error(formatFailedActionMessage('update', 'appointment'));
      // Revert the event position
      dropInfo.revert();
    }
  };

  // Handle event resize (change duration)
  const handleEventResize = async (resizeInfo) => {
    const event = resizeInfo.event;
    const appointmentId = event.id;
    const newStart = event.start;
    const newEnd = event.end;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Update appointment via API
      await axios.put(
        `${API_URL}/appointments/${appointmentId}`,
        {
          appointmentDate: newStart.toISOString(),
          endTime: newEnd.toISOString(),
          duration: Math.round((newEnd - newStart) / 60000), // Convert to minutes
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success(formatSuccessActionMessage('duration updated', 'Appointment'));
      fetchAppointments(); // Refresh to get updated data
    } catch (err) {
      console.error('Error updating appointment:', err);
      toast.error(formatFailedActionMessage('update', 'appointment duration'));
      // Revert the resize
      resizeInfo.revert();
    }
  };

  // Handle filter dialog open
  const handleFilterDialogOpen = () => {
    setFilterDialogOpen(true);
  };

  // Handle filter dialog close
  const handleFilterDialogClose = () => {
    setFilterDialogOpen(false);
  };

  // Handle filter change
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  // Apply filters
  const applyFilters = () => {
    handleFilterDialogClose();
    fetchAppointments();
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      clinicId: '',
      doctorId: '',
      status: '',
    });
  };

  // Handle time settings change
  const handleTimeSettingChange = (dayKey, field, value) => {
    setTimeSettings(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        [field]: value
      }
    }));
  };

  // Save time settings to localStorage
  const saveTimeSettings = () => {
    try {
      localStorage.setItem('calendarTimeSettings', JSON.stringify(timeSettings));
      toast.success(formatSuccessActionMessage('saved', 'Time settings'));
      setTimeSettingsDialogOpen(false);
      // Force calendar to refresh with new settings
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.render();
      }
    } catch (err) {
      console.error('Error saving time settings:', err);
      toast.error(formatFailedActionMessage('save', 'time settings'));
    }
  };

  // Handle specific date setting change (updates temporary state in dialog)
  const handleSpecificDateSettingChange = (field, value) => {
    setTempDateSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save specific date settings
  const saveSpecificDateSettings = () => {
    if (!selectedSpecificDate) {
      toast.error('Date is required');
      return;
    }
    
    try {
      const dateStr = formatDateForStorage(selectedSpecificDate);
      
      // Use temporary state values (from tempDateSettings) to save
      const updatedSettings = {
        ...specificDateSettings,
        [dateStr]: {
          isOpen: tempDateSettings.isOpen,
          startTime: tempDateSettings.startTime,
          endTime: tempDateSettings.endTime,
        }
      };
      
      // Update state
      setSpecificDateSettings(updatedSettings);
      
      // Save to localStorage
      localStorage.setItem('calendarSpecificDateSettings', JSON.stringify(updatedSettings));
      
      toast.success(formatSuccessActionMessage('saved', 'Date-specific time settings'));
      setSpecificDateDialogOpen(false);
      setSelectedSpecificDate(null);
      setTempDateSettings({ isOpen: true, startTime: '09:00', endTime: '18:00' });
      
      // Force calendar to refresh with new settings - need to wait for state update
      // Use multiple timeouts to ensure state is updated and calendar re-renders
      setTimeout(() => {
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          // Force re-render to apply new CSS classes from slotLaneClassNames
          calendarApi.render();
        }
      }, 50);
      
      setTimeout(() => {
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          // Also try to update the view to force recalculation of slot classes
          const currentDate = calendarApi.getDate();
          calendarApi.gotoDate(currentDate);
          // Force another render after navigation
          setTimeout(() => {
            calendarApi.render();
          }, 50);
        }
      }, 150);
    } catch (err) {
      console.error('Error saving specific date settings:', err);
      toast.error(formatFailedActionMessage('save', 'date-specific time settings'));
    }
  };

  // Delete specific date setting
  const deleteSpecificDateSetting = () => {
    if (!selectedSpecificDate) return;
    
    const dateStr = formatDateForStorage(selectedSpecificDate);
    const newSettings = { ...specificDateSettings };
    delete newSettings[dateStr];
    setSpecificDateSettings(newSettings);
    
    try {
      localStorage.setItem('calendarSpecificDateSettings', JSON.stringify(newSettings));
      toast.success('Date-specific setting removed');
      setSpecificDateDialogOpen(false);
      setSelectedSpecificDate(null);
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.render();
      }
    } catch (err) {
      console.error('Error deleting specific date setting:', err);
      toast.error(formatFailedActionMessage('delete', 'date-specific setting'));
    }
  };

  // Open specific date dialog
  const handleOpenSpecificDateDialog = (date = null) => {
    const selectedDate = date || new Date();
    setSelectedSpecificDate(selectedDate);
    
    // Load existing settings for this date or use defaults
    const dateStr = formatDateForStorage(selectedDate);
    const existingSettings = specificDateSettings[dateStr];
    if (existingSettings) {
      setTempDateSettings({
        isOpen: existingSettings.isOpen ?? true,
        startTime: existingSettings.startTime || '09:00',
        endTime: existingSettings.endTime || '18:00',
      });
    } else {
      // Use day of week defaults
      const dayOfWeek = selectedDate.getDay();
      const dayKey = DAY_NAMES.find(d => d.value === dayOfWeek)?.key;
      if (dayKey && timeSettings[dayKey]) {
        const daySettings = timeSettings[dayKey];
        setTempDateSettings({
          isOpen: daySettings.isOpen ?? true,
          startTime: daySettings.startTime || '09:00',
          endTime: daySettings.endTime || '18:00',
        });
      } else {
        setTempDateSettings({ isOpen: true, startTime: '09:00', endTime: '18:00' });
      }
    }
    
    setSpecificDateDialogOpen(true);
  };

  // Reset time settings to defaults
  const resetTimeSettings = () => {
    setTimeSettings(defaultTimeSettings);
  };

  // Get time settings for a specific date (checks specific date first, then day of week)
  const getTimeSettingsForDate = (date) => {
    if (!date) return null;
    
    const dateStr = formatDateForStorage(date);
    
    // Check if there's a specific date setting
    if (specificDateSettings && specificDateSettings[dateStr]) {
      const specific = specificDateSettings[dateStr];
      // If isOpen is explicitly false, return null (closed day)
      if (specific.isOpen === false) {
        return null;
      }
      // If isOpen is true or undefined, return the settings
      if (specific.isOpen !== false) {
        return {
          startTime: specific.startTime || '09:00',
          endTime: specific.endTime || '18:00',
        };
      }
    }
    
    // Fall back to day of week settings
    const dayOfWeek = date.getDay();
    const dayKey = DAY_NAMES.find(d => d.value === dayOfWeek)?.key;
    if (dayKey && timeSettings && timeSettings[dayKey]) {
      const daySettings = timeSettings[dayKey];
      if (daySettings.isOpen) {
        return {
          startTime: daySettings.startTime || '09:00',
          endTime: daySettings.endTime || '18:00',
        };
      }
    }
    
    return null;
  };

  // Format date as YYYY-MM-DD for storage
  const formatDateForStorage = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Convert time settings to FullCalendar businessHours format
  // Note: FullCalendar's businessHours doesn't support specific dates directly,
  // so we'll use a workaround with eventOverlap and slot constraints
  const getBusinessHours = () => {
    const businessHoursArray = [];
    
    DAY_NAMES.forEach(day => {
      const daySettings = timeSettings[day.key];
      if (daySettings.isOpen) {
        businessHoursArray.push({
          daysOfWeek: [day.value],
          startTime: daySettings.startTime,
          endTime: daySettings.endTime,
        });
      }
    });
    
    return businessHoursArray.length > 0 ? businessHoursArray : [{
      daysOfWeek: [1, 2, 3, 4, 5, 6],
      startTime: '09:00',
      endTime: '18:00',
    }];
  };

  // Calculate min and max slot times from all days
  const getSlotTimes = () => {
    const allTimes = [];
    DAY_NAMES.forEach(day => {
      const daySettings = timeSettings[day.key];
      if (daySettings.isOpen) {
        allTimes.push(daySettings.startTime);
        allTimes.push(daySettings.endTime);
      }
    });
    
    if (allTimes.length === 0) {
      return { min: '08:00:00', max: '20:00:00' };
    }
    
    // Convert to comparable format and find min/max
    const times = allTimes.map(time => {
      const [hours, minutes] = time.split(':');
      return parseInt(hours) * 60 + parseInt(minutes);
    });
    
    const minMinutes = Math.min(...times);
    const maxMinutes = Math.max(...times);
    
    // Subtract 1 hour from min, add 1 hour to max for padding
    const minHour = Math.max(0, Math.floor((minMinutes - 60) / 60));
    const minMin = (minMinutes - 60) % 60;
    const maxHour = Math.min(23, Math.floor((maxMinutes + 60) / 60));
    const maxMin = (maxMinutes + 60) % 60;
    
    return {
      min: `${String(minHour).padStart(2, '0')}:${String(minMin).padStart(2, '0')}:00`,
      max: `${String(maxHour).padStart(2, '0')}:${String(maxMin).padStart(2, '0')}:00`
    };
  };

  const slotTimes = getSlotTimes();

  // Check if a day is a working day (has business hours)
  // This function needs to use current state values
  const isWorkingDay = useCallback((date) => {
    if (!date) return false;
    const dateStr = formatDateForStorage(date);
    
    // Check specific date settings first
    if (specificDateSettings && specificDateSettings[dateStr]) {
      const specific = specificDateSettings[dateStr];
      return specific.isOpen !== false; // Return true if open or undefined
    }
    
    // Fall back to day of week settings
    const dayOfWeek = date.getDay();
    const dayKey = DAY_NAMES.find(d => d.value === dayOfWeek)?.key;
    if (dayKey && timeSettings && timeSettings[dayKey]) {
      return timeSettings[dayKey].isOpen === true;
    }
    
    return false;
  }, [specificDateSettings, timeSettings, DAY_NAMES]);

  // Add CSS class to non-working days (for month view)
  const dayCellClassNames = useCallback((dayInfo) => {
    const classes = [];
    if (dayInfo && dayInfo.date) {
      if (!isWorkingDay(dayInfo.date)) {
        classes.push('fc-non-working-day');
      }
    }
    return classes;
  }, [isWorkingDay, DAY_NAMES]);

  // Add CSS class to non-working days in timeGrid views (week/day view)
  const dayHeaderClassNames = useCallback((dayInfo) => {
    const classes = [];
    if (dayInfo && dayInfo.date) {
      if (!isWorkingDay(dayInfo.date)) {
        classes.push('fc-non-working-day-header');
      }
    }
    return classes;
  }, [isWorkingDay]);

  // Add CSS class to timeGrid day cells
  const dayHeaderContent = (dayInfo) => {
    // Return default content, but we'll style via CSS
    return dayInfo.text;
  };

  // Add CSS classes to time slots based on whether they're within business hours
  // NOTE: slotLaneClassNames doesn't work reliably for determining dates in week view,
  // so we rely on applyClosedSlotClasses useEffect instead
  const slotLaneClassNames = useCallback((slotInfo) => {
    // Return empty - we handle styling in applyClosedSlotClasses
    return [];
  }, []);

  // Check if a time selection is allowed based on date-specific or weekly settings
  const selectAllow = (selectInfo) => {
    if (!selectInfo || !selectInfo.start) {
      return true; // Allow selection if we can't determine the date
    }
    
    const date = new Date(selectInfo.start);
    const timeSettings = getTimeSettingsForDate(date);
    
    if (!timeSettings) {
      // Date is closed - show message and block selection
      toast.error('This date is closed for appointments');
      return false;
    }
    
    // Get the time of the selected slot
    const selectedHour = date.getHours();
    const selectedMinute = date.getMinutes();
    const selectedTimeMinutes = selectedHour * 60 + selectedMinute;
    
    // Parse business hours
    const [startHour, startMin] = timeSettings.startTime.split(':').map(Number);
    const [endHour, endMin] = timeSettings.endTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    
    // Check if start time is within business hours
    if (selectedTimeMinutes < startMinutes || selectedTimeMinutes >= endMinutes) {
      // Selection is outside business hours - show message
      const startTimeStr = timeSettings.startTime;
      const endTimeStr = timeSettings.endTime;
      toast.error(`This time slot is closed. Working hours: ${startTimeStr} - ${endTimeStr}`);
      return false;
    }
    
    // If there's an end time, check it too
    if (selectInfo.end) {
      const endDate = new Date(selectInfo.end);
      const endHour = endDate.getHours();
      const endMinute = endDate.getMinutes();
      const endTimeMinutes = endHour * 60 + endMinute;
      
      if (endTimeMinutes > endMinutes) {
        const endTimeStr = timeSettings.endTime;
        toast.error(`Selection extends beyond working hours. Closing time: ${endTimeStr}`);
        return false;
      }
    }
    
    return true; // Selection is within business hours
  };

  if (loading && appointments.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Appointment Calendar
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => setTimeSettingsDialogOpen(true)}
              sx={{ mr: 2 }}
            >
              Weekly Settings
            </Button>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => {
                // Get the currently visible date from the calendar
                let dateToUse = null;
                if (calendarRef.current) {
                  const calendarApi = calendarRef.current.getApi();
                  const currentDate = calendarApi.getDate();
                  if (currentDate) {
                    dateToUse = currentDate;
                  }
                }
                handleOpenSpecificDateDialog(dateToUse);
              }}
              sx={{ mr: 2 }}
            >
              Date Settings
            </Button>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={handleFilterDialogOpen}
              sx={{ mr: 2 }}
            >
              Filter
            </Button>
            <Button
              variant="outlined"
              startIcon={<ViewListIcon />}
              onClick={() => navigate('/appointments')}
              sx={{ mr: 2 }}
            >
              List View
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchAppointments}
              sx={{ mr: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Refresh'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/appointments/add')}
            >
              Add Appointment
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ height: 'auto', minHeight: 700 }}>
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin, momentPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
              }}
              height="auto"
              events={appointments}
              editable={true}
              selectable={currentView !== 'dayGridMonth'}
              selectLongPressDelay={0}
              selectMirror={true}
              dayMaxEvents={true}
              weekends={true}
              eventClick={handleEventClick}
              eventDidMount={handleEventDidMount}
              eventContent={renderEventContent}
              dateClick={currentView === 'dayGridMonth' ? handleDateClick : undefined}
              select={handleDateSelect}
              unselectAuto={true}
              eventDrop={handleEventDrop}
              eventResize={handleEventResize}
              viewDidMount={handleViewChange}
              eventDisplay="block"
              eventTimeFormat={timeFormat === '12h'
                ? { hour: 'numeric', minute: '2-digit', meridiem: 'short' }
                : { hour: '2-digit', minute: '2-digit', hour12: false }}
              slotLabelFormat={timeFormat === '12h'
                ? { hour: 'numeric', minute: '2-digit', meridiem: 'short' }
                : { hour: '2-digit', minute: '2-digit', hour12: false }}
              slotMinTime={slotTimes.min}
              slotMaxTime={slotTimes.max}
              allDaySlot={false}
              businessHours={getBusinessHours()}
              selectAllow={selectAllow}
              dayCellClassNames={dayCellClassNames}
              dayHeaderClassNames={dayHeaderClassNames}
              dayHeaderContent={dayHeaderContent}
              slotLaneClassNames={slotLaneClassNames}
              slotDuration="00:30:00"
              slotLabelInterval="01:00:00"
              views={{
                timeGridWeek: {
                  slotMinTime: "08:00:00",
                  slotMaxTime: "22:00:00",
                  scrollTime: "08:00:00",
                  slotDuration: "00:30:00",
                },
                timeGridDay: {
                  slotMinTime: "08:00:00",
                  slotMaxTime: "22:00:00",
                  scrollTime: "08:00:00",
                  slotDuration: "00:30:00",
                },
              }}
            />
          </Box>
        </Paper>
      </Box>

      {/* Event Details Dialog */}
      <Dialog
        open={eventDialogOpen}
        onClose={handleEventDialogClose}
        maxWidth="sm"
        fullWidth
        ModalProps={{
          keepMounted: false,
          disableEnforceFocus: false,
          disableAutoFocus: false,
        }}
      >
        {selectedEvent && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" component="span">
                  Appointment Details
                </Typography>
                <Button
                  onClick={handleAddTreatment}
                  color="secondary"
                  variant="contained"
                  startIcon={<MedicalServicesIcon />}
                  size="small"
                >
                  Treatment
                </Button>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="h6">{selectedEvent.patientName}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {new Date(selectedEvent.start).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(selectedEvent.start).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: timeFormat === '12h'
                    })} - {new Date(selectedEvent.end).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: timeFormat === '12h'
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    Doctor:
                  </Typography>
                  <Typography variant="body1">
                    {selectedEvent.doctorName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    Clinic:
                  </Typography>
                  <Typography variant="body1">
                    {selectedEvent.clinicName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    Type:
                  </Typography>
                  <Typography variant="body1">
                    {selectedEvent.type?.charAt(0).toUpperCase() + selectedEvent.type?.slice(1) || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    Status:
                  </Typography>
                  <Chip
                    label={selectedEvent.status?.charAt(0).toUpperCase() + selectedEvent.status?.slice(1) || 'N/A'}
                    color={
                      selectedEvent.status === 'completed' ? 'success' :
                      selectedEvent.status === 'confirmed' ? 'primary' :
                      selectedEvent.status === 'cancelled' ? 'error' :
                      selectedEvent.status === 'no-show' ? 'warning' : 'default'
                    }
                    size="small"
                  />
                </Grid>
                {selectedEvent.reasonForVisit && (
                  <Grid item xs={12}>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      Reason for Visit:
                    </Typography>
                    <Typography variant="body2">
                      {selectedEvent.reasonForVisit}
                    </Typography>
                  </Grid>
                )}
                {selectedEvent.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      Notes:
                    </Typography>
                    <Typography variant="body2">
                      {selectedEvent.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleViewPatient} color="primary">
                Card
              </Button>
              <Button onClick={handleEventDialogClose}>
                Close
              </Button>
              <Button onClick={handleEditAppointment} color="primary">
                Edit
              </Button>
              <Button onClick={handleViewAppointment} color="primary" variant="contained">
                View Details
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Filter Dialog */}
      <Dialog
        open={filterDialogOpen}
        onClose={handleFilterDialogClose}
        maxWidth="sm"
        fullWidth
        ModalProps={{
          keepMounted: false,
          disableEnforceFocus: false,
          disableAutoFocus: false,
        }}
      >
        <DialogTitle>Filter Appointments</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="clinic-filter-label">Clinic</InputLabel>
                <Select
                  labelId="clinic-filter-label"
                  id="clinicId"
                  name="clinicId"
                  value={filters.clinicId}
                  onChange={handleFilterChange}
                  label="Clinic"
                >
                  <MenuItem value="">All Clinics</MenuItem>
                  {clinics.map((clinic) => (
                    <MenuItem key={clinic._id} value={clinic._id}>
                      {clinic.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="doctor-filter-label">Doctor</InputLabel>
                <Select
                  labelId="doctor-filter-label"
                  id="doctorId"
                  name="doctorId"
                  value={filters.doctorId}
                  onChange={handleFilterChange}
                  label="Doctor"
                >
                  <MenuItem value="">All Doctors</MenuItem>
                  {doctors.map((doctor) => (
                    <MenuItem key={doctor._id} value={doctor._id}>
                      {`${doctor.firstName} ${doctor.lastName}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  id="status"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {APPOINTMENT_STATUSES.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetFilters} color="inherit">
            Reset
          </Button>
          <Button onClick={handleFilterDialogClose} color="inherit">
            Cancel
          </Button>
          <Button onClick={applyFilters} color="primary" variant="contained">
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Time Settings Dialog */}
      <Dialog
        open={timeSettingsDialogOpen}
        onClose={() => setTimeSettingsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        ModalProps={{
          keepMounted: false,
          disableEnforceFocus: false,
          disableAutoFocus: false,
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Calendar Time Settings</Typography>
            <Button
              onClick={resetTimeSettings}
              color="inherit"
              size="small"
            >
              Reset to Defaults
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure the time range for each day of the week. These settings will be applied when viewing the day or week calendar.
          </Typography>
          <Grid container spacing={3}>
            {DAY_NAMES.map((day) => {
              const daySettings = timeSettings[day.key];
              return (
                <Grid item xs={12} key={day.key}>
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        {day.label}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={daySettings.isOpen}
                            onChange={(e) => handleTimeSettingChange(day.key, 'isOpen', e.target.checked)}
                          />
                        }
                        label={daySettings.isOpen ? 'Open' : 'Closed'}
                      />
                    </Box>
                    {daySettings.isOpen && (
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Start Time"
                            type="time"
                            value={daySettings.startTime}
                            onChange={(e) => handleTimeSettingChange(day.key, 'startTime', e.target.value)}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            inputProps={{
                              step: 300, // 5 minutes
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="End Time"
                            type="time"
                            value={daySettings.endTime}
                            onChange={(e) => handleTimeSettingChange(day.key, 'endTime', e.target.value)}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            inputProps={{
                              step: 300, // 5 minutes
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimeSettingsDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={saveTimeSettings} color="primary" variant="contained">
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Specific Date Time Settings Dialog */}
      <Dialog
        open={specificDateDialogOpen}
        onClose={() => {
          setSpecificDateDialogOpen(false);
          setSelectedSpecificDate(null);
        }}
        maxWidth="sm"
        fullWidth
        ModalProps={{
          keepMounted: false,
          disableEnforceFocus: false,
          disableAutoFocus: false,
        }}
      >
        <DialogTitle>
          <Typography variant="h6">Specific Date Time Settings</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Set custom time range for a specific date. This will override the weekly settings for that date.
          </Typography>
          {selectedSpecificDate && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  value={formatDateForStorage(selectedSpecificDate)}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value + 'T00:00:00'); // Add time to avoid timezone issues
                    setSelectedSpecificDate(newDate);
                    
                    // Load existing settings for the new date
                    const dateStr = e.target.value;
                    const existingSettings = specificDateSettings[dateStr];
                    if (existingSettings) {
                      setTempDateSettings({
                        isOpen: existingSettings.isOpen ?? true,
                        startTime: existingSettings.startTime || '09:00',
                        endTime: existingSettings.endTime || '18:00',
                      });
                    } else {
                      // Use day of week defaults
                      const dayOfWeek = newDate.getDay();
                      const dayKey = DAY_NAMES.find(d => d.value === dayOfWeek)?.key;
                      if (dayKey && timeSettings[dayKey]) {
                        const daySettings = timeSettings[dayKey];
                        setTempDateSettings({
                          isOpen: daySettings.isOpen ?? true,
                          startTime: daySettings.startTime || '09:00',
                          endTime: daySettings.endTime || '18:00',
                        });
                      } else {
                        setTempDateSettings({ isOpen: true, startTime: '09:00', endTime: '18:00' });
                      }
                    }
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={tempDateSettings.isOpen}
                      onChange={(e) => handleSpecificDateSettingChange('isOpen', e.target.checked)}
                    />
                  }
                  label={tempDateSettings.isOpen ? 'Open' : 'Closed'}
                />
              </Grid>
              {tempDateSettings.isOpen && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Start Time"
                      type="time"
                      value={tempDateSettings.startTime}
                      onChange={(e) => handleSpecificDateSettingChange('startTime', e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        step: 300, // 5 minutes
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="End Time"
                      type="time"
                      value={tempDateSettings.endTime}
                      onChange={(e) => handleSpecificDateSettingChange('endTime', e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        step: 300, // 5 minutes
                      }}
                    />
                  </Grid>
                </>
              )}
              {selectedSpecificDate && specificDateSettings[formatDateForStorage(selectedSpecificDate)] && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={deleteSpecificDateSetting}
                    fullWidth
                  >
                    Remove Custom Settings for This Date
                  </Button>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSpecificDateDialogOpen(false);
              setSelectedSpecificDate(null);
              setTempDateSettings({ isOpen: true, startTime: '09:00', endTime: '18:00' });
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button onClick={saveSpecificDateSettings} color="primary" variant="contained">
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AppointmentCalendar;

